// ── NEEDS ATTENTION — the exceptions the agency has to act on, and nothing else ──
//
// Kevin, 2026-10-19: *"I like the idea of a dashboard but it needs to be simple"* and
// *"seeing a list with a massive amount of farm names on it is not helpful… We know who
// we need to see."* So this is NOT a roster of farms with work due. Each line is a
// condition SILAS already detects somewhere else, counted, with one plain sentence and
// an owner. A line exists only while its condition holds — it is computed live from
// the data every time the page opens, never ticked off by hand.
//
// ⚠ THIS MODULE HOLDS NO SECOND COPY OF ANY RULE. Every detector below reads a rule
// that already lives somewhere else and says where:
//   578s            the 578 board's own counts (FSA578Screen `stats`) over the status
//                   vocabulary of src/fsa578Intake.js; campaign years from
//                   src/campaign578.js `acceptedProgramYears` (passed in)
//   signed copies   api/_dropbox-sign.js SIGNED_COPY_MISSING
//   prevent plant   the ONE engine (src/claims/ppAdjuster.js adjudicateFarm +
//                   figuresByCountyCrop), the paid lines through claimExpected
//                   `currentLines` and ppStages `isPreventPlantStage`, and the approved
//                   estimate-vs-paid threshold claimCrossCheck `estimateFlagThreshold`
//                   — all INJECTED, so this file cannot grow its own version
//   John Deere      api/_jd.js staleReport's own filter (api/_attention.js repeats it
//                   byte for byte and tools/check-needs-attention.js asserts they match)
//   watchdog        pipeline_watchdog_alerts with recovered_at null — _watchdog.js's
//                   own definition of an alert still open
//   book check      the sentinel's own run row (new_count / worsened_count) — it alerts
//                   only on what is NEW or WORSE, so this line does not nag either
//   Dropbox         stage-scan `listing_errors`, the same field the pipeline board reads
//   planted crop    src/attention/plantedVsReported.js, run server-side (the John Deere
//                   tables are service-role only) by api/_attention.js?part=planted
//   unit guarantee  src/claims/unitGuaranteeFlag.js, run server-side by
//                   api/_attention.js?part=unit-guarantee — only units the module marked
//                   `below` (whole unit under its guarantee, 90% or more harvested)
//   claims          claim_alerts rows the daily claim radar left OPEN (api/_claim-radar.js,
//                   rules in src/claimRadar.js) — read through /api/market?fn=claim-radar
//
// ⚠ A SOURCE THAT COULD NOT BE READ IS NAMED, NEVER COUNTED AS ZERO. "Nothing needs
// anyone today" is only ever printed when EVERY source answered.

export const OWNERS = Object.freeze(["Mandy", "Claims", "Kevin"]);

const s = (n) => (n === 1 ? "" : "s");

// The catalogue. One sentence per line, written for the person who acts on it.
export const LINE_DEFS = Object.freeze({
  "578-unmatched": { owner: "Mandy", nav: "fsa578", source: "578s",
    say: (n) => `${n} 578${s(n)} couldn't be matched to a farmer` },
  "578-locked": { owner: "Mandy", nav: "fsa578", source: "578s",
    say: (n) => `${n} 578${s(n)} ${n === 1 ? "is" : "are"} password protected and can't be read` },
  "578-not-sent": { owner: "Mandy", nav: "fsa578", source: "578s",
    say: (n) => `${n} matched 578${s(n)} never reached Mandy` },
  "signed-copy-missing": { owner: "Mandy", nav: "fsa578", source: "documents",
    say: (n) => `${n} signed document${s(n)} ${n === 1 ? "has" : "have"} no signed copy on file` },
  "pp-held": { owner: "Claims", nav: "claims", source: "pp",
    say: (n) => `${n} prevent-plant crop${s(n)} can't be estimated without a person` },
  "pp-differs": { owner: "Claims", nav: "claims", source: "pp",
    say: (n) => `${n} prevent-plant payment${s(n)} differ${n === 1 ? "s" : ""} from our estimate by more than $25 or 1%` },
  "jd-stalled": { owner: "Kevin", nav: "fields", source: "jd",
    say: (n) => `${n} John Deere connection${s(n)} stopped syncing` },
  "feed-missed": { owner: "Kevin", nav: "synclog", source: "watchdog",
    say: (n) => `${n} data feed${s(n)} missed ${n === 1 ? "its" : "their"} run` },
  "book-check-failed": { owner: "Kevin", nav: null, source: "bookcheck",
    say: () => "The nightly check of the book against the AIP file didn't finish" },
  "book-check-new": { owner: "Kevin", nav: null, source: "bookcheck",
    say: (n) => `${n} new difference${s(n)} between the book and the AIP file` },
  "planted-vs-reported": { owner: "Mandy", nav: "fsa578", source: "planted",
    say: (n) => `${n} field${s(n)} planted to a different crop than was reported` },
  "dropbox-unreadable": { owner: "Kevin", nav: "fsa578", source: "dropbox",
    say: (n) => `${n} Dropbox folder${s(n)} couldn't be read, so document stages may be behind` },
  "unit-below-guarantee": { owner: "Claims", nav: "claims", source: "unitguarantee",
    say: (n) => `${n} insurance unit${s(n)} ${n === 1 ? "has" : "have"} a machine yield below the guarantee across the whole unit` },
  "claim-likely": { owner: "Claims", nav: "claims", source: "claimradar",
    say: (n) => `${n} likely claim${s(n)} with nothing filed or paid` },
});

export const SOURCE_LABEL = Object.freeze({
  "578s": "578s", documents: "signed documents", pp: "prevent plant",
  jd: "John Deere connections", watchdog: "data feeds", bookcheck: "the book check",
  dropbox: "Dropbox folders", planted: "planting against the reported crops",
  claimradar: "claim alerts", unitguarantee: "unit yields against their guarantees",
});

// ── 578s ──────────────────────────────────────────────────────────────────────
// The SAME predicates FSA578Screen's `stats` uses: unmatched keys off farmer_id (the
// source of truth), excluding a locked file, which has its own line. "Never reached
// Mandy" = matched and not forwarded — a send fails closed to exactly this state
// (src/fsa578Intake.js forwardedPatch: forwarded only on an accepted send).
// Only the LIVE campaigns: a CY2025 row nobody will ever key is not today's work. A
// row with no program year is kept — we cannot place it, and dropping it would hide it.
export function attention578(rows = [], liveYears = []) {
  const years = new Set((liveYears || []).map(Number));
  const live = (rows || []).filter((r) => r && (r.tax_year == null || years.has(Number(r.tax_year))));
  const locked = live.filter((r) => r.status === "password_protected");
  const unmatched = live.filter((r) => !r.farmer_id && r.status !== "password_protected");
  const notSent = live.filter((r) => r.farmer_id && !r.forwarded_to_mandy && r.status !== "password_protected");
  return { unmatched, locked, notSent };
}

// ── signed documents ─────────────────────────────────────────────────────────
export const SIGNED_COPY_MISSING = "signed_copy_missing";
export function signedCopyMissing(docs = []) {
  return (docs || []).filter((d) => d && d.signature_status === SIGNED_COPY_MISSING && !d.superseded_by);
}

// ── prevent plant ────────────────────────────────────────────────────────────
// One cell = (policy, county, crop) — the grain the engine's figure and the carrier's
// payment both resolve to. HELD = the engine refused a figure (unpriced, or eligibility
// unknown with acres it could not place). DIFFERS = the carrier paid and the gap from the
// engine's figure is over the approved threshold. A cell that was ROLLED (acres borrowed
// from another crop) is compared at the POLICY level instead: a carrier books rolled acres
// under the lending crop's line, so a per-cell gap there is bookkeeping, not a difference.
export function ppAttention({ policies = [], units = [], coverages = [], eligibility = [], prices = [],
  lossLines = [], cropYear, deps } = {}) {
  const d = deps || {};
  const need = ["buildPpCells", "buildModel", "buildPriceIndex", "resolvePpFactor", "ppStateFips",
    "adjudicateFarm", "figuresByCountyCrop", "currentLines", "isPreventPlantStage", "estimateFlagThreshold", "cropOf"];
  const missing = need.filter((k) => typeof d[k] !== "function");
  if (missing.length) throw new Error("prevent-plant rules not supplied: " + missing.join(", "));
  const up = (v) => String(v == null ? "" : v).trim().toUpperCase();
  const yr = Number(cropYear);
  const yearPols = (policies || []).filter((p) => Number(p.crop_year) === yr);
  const polById = new Map(yearPols.map((p) => [p.id, p]));
  const cellOf = (pid, county, crop) => [pid, up(county), up(crop)].join("|");

  const built = d.buildPpCells({ units, coverages, eligibility, policies: yearPols,
    priceIndex: d.buildPriceIndex(prices, d.cropOf), stateFipsOf: d.ppStateFips, cropYear: yr });
  const byFarm = new Map();
  for (const [pid, g] of built) {
    const fid = g.policy.farmer_id || pid;
    if (!byFarm.has(fid)) byFarm.set(fid, []);
    byFarm.get(fid).push({ policy: { ...g.policy, id: pid }, cells: g.cells,
      model: d.buildModel({ cells: g.cells, cropYear: yr, state: g.policy.state,
        resolveFactor: (input) => d.resolvePpFactor(input) }) });
  }
  const fig = new Map();       // cell -> { figure, held, why, ppAcres, rolled }
  for (const groups of byFarm.values()) {
    for (const [pid, out] of d.adjudicateFarm(groups)) {
      for (const [ck, v] of d.figuresByCountyCrop(out) || []) {
        const [county, crop] = ck.split("|");
        fig.set(cellOf(pid, county, crop), { ...v, policyId: pid, county, crop });
      }
    }
  }
  // paid, per cell — current worksheet generation, prevent-plant stages only
  const covIif = new Map();
  for (const c of coverages || []) if (c.iif_key != null && polById.has(c.policy_id)) covIif.set(String(c.iif_key), c);
  const paid = new Map();
  const { kept } = d.currentLines((lossLines || []).filter((l) => Number(l.crop_year) === yr));
  for (const l of kept || []) {
    if (!d.isPreventPlantStage(l.stage_code)) continue;
    const c = covIif.get(String(l.iif_key));
    if (!c) continue;
    const k = cellOf(c.policy_id, c.county, c.crop);
    paid.set(k, Math.round(((paid.get(k) || 0) + (Number(l.aip_indemnity) || 0)) * 100) / 100);
  }
  const held = [], differs = [];
  const rolledPolicies = new Set([...fig.values()].filter((v) => v.rolled).map((v) => v.policyId));
  for (const [k, v] of fig) {
    if (v.held && (Number(v.ppAcres) || 0) > 0.005) {
      held.push({ key: k, policyId: v.policyId, county: v.county, crop: v.crop, ppAcres: v.ppAcres, why: v.held });
      continue;
    }
    if (rolledPolicies.has(v.policyId) || v.figure == null || !paid.has(k)) continue;
    const p = paid.get(k);
    if (d.estimateFlagThreshold(v.figure, p - v.figure).flag)
      differs.push({ key: k, policyId: v.policyId, county: v.county, crop: v.crop, estimate: v.figure, paid: p });
  }
  // Rolled policies: one comparison over the whole policy, only when nothing on it is held.
  for (const pid of rolledPolicies) {
    const cells = [...fig.entries()].filter(([, v]) => v.policyId === pid);
    if (cells.some(([, v]) => v.figure == null)) continue;
    const est = cells.reduce((t, [, v]) => t + v.figure, 0);
    const pd = [...paid.entries()].filter(([k]) => k.startsWith(pid + "|"));
    if (!pd.length) continue;
    const p = pd.reduce((t, [, v]) => t + v, 0);
    if (d.estimateFlagThreshold(est, p - est).flag)
      differs.push({ key: pid, policyId: pid, county: null, crop: "whole policy (acres rolled between crops)",
        estimate: Math.round(est * 100) / 100, paid: Math.round(p * 100) / 100 });
  }
  return { held, differs };
}

// ── John Deere ───────────────────────────────────────────────────────────────
// The list arrives already filtered by api/_attention.js (staleReport's own query).
export function jdStalled(conns = []) {
  return (conns || []).filter(Boolean);
}

// ── planted vs reported ─────────────────────────────────────────────────────────
// The flags arrive already decided by api/_attention.js?part=planted, which runs
// src/attention/plantedVsReported.js — the rule is there, never here.
export function plantedMismatch(flags = []) {
  return (flags || []).filter(Boolean);
}

// ── whole units below their guarantee ────────────────────────────────────────
// Decided by api/_attention.js?part=unit-guarantee (src/claims/unitGuaranteeFlag.js). Only a
// unit the module marked `below` is a line; "not enough harvested yet" is not work for anyone.
export function unitsBelowGuarantee(units = []) {
  return (units || []).filter((u) => u && u.status === "below");
}

// ── data feeds ───────────────────────────────────────────────────────────────
export function feedsMissed(alerts = []) {
  return (alerts || []).filter((a) => a && a.recovered_at == null);
}

// ── the nightly book check ───────────────────────────────────────────────────
// The latest run decides. A run that did not end `ok`, or no run inside the window,
// is one line; a clean run's NEW or WORSE count is the other. Standing findings are
// not repeated here every morning — that is the nag Kevin ruled out. Neither line has a
// page to open (`nav: null`): the Book Audit page was removed 2026-11-02 and the
// sentinel's own email lists the findings.
export const BOOK_CHECK_MAX_AGE_H = 36;
export function bookCheck(runs = [], now = new Date()) {
  const sorted = (runs || []).filter(Boolean)
    .sort((a, b) => String(b.started_at || "").localeCompare(String(a.started_at || "")));
  const last = sorted[0] || null;
  const ageH = last ? (now.getTime() - new Date(last.started_at).getTime()) / 3.6e6 : Infinity;
  const failed = !last || last.status !== "ok" || !(ageH <= BOOK_CHECK_MAX_AGE_H);
  const fresh = failed ? 0 : (Number(last.new_count) || 0) + (Number(last.worsened_count) || 0);
  return { last, failed, fresh };
}

// ── Dropbox ──────────────────────────────────────────────────────────────────
export function dropboxUnreadable(scan) {
  return scan && Array.isArray(scan.listing_errors) ? scan.listing_errors.filter(Boolean) : [];
}

// ── likely claims ────────────────────────────────────────────────────────────
// The radar has already decided each row (shipped claim rules, nothing on file). This
// line shows the EXPECTED tier only: a trending PRF interval is still open, so it is
// news for the daily email, not work for anyone yet. Soonest deadline first.
export function claimsLikely(alerts = []) {
  return (alerts || []).filter((a) => a && a.status !== "cleared" && a.tier === "expected")
    .sort((a, b) => String(a.deadline || "9999").localeCompare(String(b.deadline || "9999"))
      || (Number(b.amount) || 0) - (Number(a.amount) || 0));
}

// ── assembling the page ──────────────────────────────────────────────────────
// `found` maps a LINE key to its items; `status` maps a SOURCE to "ok" | "loading" |
// { error }. A line is shown only when its items are non-empty AND its source answered.
export function buildLines(found = {}, status = {}) {
  const lines = [];
  for (const [key, def] of Object.entries(LINE_DEFS)) {
    if (status[def.source] !== "ok") continue;
    // A line is usually its items; a source that reports a COUNT without a row per item
    // (the book check states how many findings are new, not which) passes { count, items }.
    const v = found[key];
    const items = Array.isArray(v) ? v : (v && Array.isArray(v.items) ? v.items : []);
    const count = Array.isArray(v) ? v.length : (v && Number(v.count) > 0 ? Number(v.count) : 0);
    if (!(count > 0)) continue;
    lines.push({ key, owner: def.owner, nav: def.nav, count, sentence: def.say(count), items });
  }
  const sources = [...new Set(Object.values(LINE_DEFS).map((d) => d.source))];
  const loading = sources.filter((src) => status[src] === "loading" || status[src] == null);
  const unchecked = sources.filter((src) => status[src] && typeof status[src] === "object")
    .map((src) => ({ source: src, label: SOURCE_LABEL[src] || src, why: String(status[src].error || "could not be read") }));
  return { lines, loading, unchecked, allClear: !lines.length && !loading.length && !unchecked.length };
}

export function filterByOwner(lines = [], owner = "All") {
  if (!owner || owner === "All") return lines || [];
  return (lines || []).filter((l) => l.owner === owner);
}
